import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { BudgetAmounts } from 'types/api/budgets.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BudgetContext } from 'contexts';

import { useFetch } from 'hooks';

import { checkParams } from 'helpers/checkParams';

import { Notification } from 'components/Notification';
import { TabType } from 'components/Tabs';

import { Code } from './components/Code';
import { Fabric } from './components/Fabric';
import { Gender } from './components/Gender';
import { Marketing } from './components/Marketing';

const useDetails = () => {
  const { t } = useTranslation();

  const { id, gender } = useParams();

  const { season, marketLine, zone } = useAppSelector((state) => state.settings);

  const {
    budgetStatus,
    budgetStatusSet,
    refreshHeader,
    params: contextParams,
  } = useContext(BudgetContext);

  const [amounts, amountsSet] = useState<BudgetAmounts>({
    code: 0,
    marketing: 0,
    fabric: 0,
    gender: 0,
  });
  const [isModalVisible, isModalVisibleSet] = useState<boolean>(false);
  const [isLoading, isLoadingSet] = useState<boolean>(false);

  const params = {
    season,
    seasonLine: marketLine,
    zone,
    genderCode: gender!,
    budgetNum: id!,
    type: contextParams?.type || '',
  };

  const { content } = useFetch(
    checkParams(params) ? () => budgetDataService.getAmountOfLists(params) : undefined,
    [season, marketLine, zone, gender, id, contextParams?.type]
  );

  const sendBudget = () => {
    if (zone && id) {
      isLoadingSet(true);
      isModalVisibleSet(false);

      budgetDataService
        .sendBudget(zone, id)
        .then(() => {
          // Raw backend value, not the translated label: the quantity lock compares it to 'sent'
          budgetStatusSet('Sent');
          refreshHeader();
          isLoadingSet(false);
        })
        .catch(() => {
          toast.error(t('budget.sendingFailed'));
          isLoadingSet(false);
        });
    }
  };

  const tabs: TabType[] = [
    {
      id: 1,
      title: (
        <Notification amount={amounts?.code} color="red" notificationUnRead={false}>
          {t('common.code')}
        </Notification>
      ),
      content: (selected: boolean) => <Code isActive={selected} />,
    },
    {
      id: 2,
      title: (
        <Notification amount={amounts?.fabric} color="red" notificationUnRead={false}>
          {t('catalogue.fabric')}
        </Notification>
      ),
      content: (selected: boolean) => <Fabric isActive={selected} />,
    },
    {
      id: 3,
      title: (
        <Notification amount={amounts?.marketing} color="red" notificationUnRead={false}>
          {t('statistics.marketing')}
        </Notification>
      ),
      content: (selected: boolean) => <Marketing isActive={selected} />,
    },
    {
      id: 4,
      title: (
        <Notification amount={amounts?.gender} color="red" notificationUnRead={false}>
          {t('catalogue.gender')}
        </Notification>
      ),
      content: (selected: boolean) => <Gender isActive={selected} />,
    },
  ];

  useEffect(() => {
    if (content) {
      amountsSet(content);
    }
  }, [content]);

  return {
    tabs,
    isModalVisible,
    isModalVisibleSet,
    isLoading,
    budgetStatus,
    sendBudget,
  };
};

export { useDetails };
